import { useEffect, useState } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import {
  ResourceTable,
  type Field,
} from "@project/admin/components/resource-table";
import { supabase } from "@project/lib/data-client";
import { Repeat, IndianRupee, TrendingUp, CalendarClock } from "lucide-react";

const fields: Field[] = [
  {
    name: "donor_id",
    label: "Donor",
    required: true,
    showInTable: true,
    lookup: {
      table: "donors",
      labelField: "full_name",
      secondaryField: "mobile",
    },
  },
  {
    name: "donation_type_id",
    label: "Donation Type",
    showInTable: true,
    lookup: { table: "donation_types", labelField: "name" },
  },
  {
    name: "campaign_id",
    label: "Campaign (optional)",
    lookup: { table: "campaigns", labelField: "title" },
  },
  {
    name: "amount",
    label: "Amount (₹)",
    type: "number",
    required: true,
    showInTable: true,
  },
  {
    name: "frequency",
    label: "Frequency",
    type: "select",
    required: true,
    showInTable: true,
    options: ["monthly", "quarterly", "yearly"].map(v => ({
      value: v,
      label: v,
    })),
  },
  { name: "start_date", label: "Start Date", type: "date", required: true },
  { name: "end_date", label: "End Date", type: "date" },
  {
    name: "next_payment_date",
    label: "Next Payment",
    type: "date",
    showInTable: true,
  },
  { name: "last_payment_date", label: "Last Payment", type: "date" },
  { name: "razorpay_subscription_id", label: "Razorpay Subscription ID" },
  {
    name: "status",
    label: "Status",
    type: "select",
    showInTable: true,
    options: ["active", "paused", "cancelled", "completed"].map(v => ({
      value: v,
      label: v,
    })),
  },
  { name: "notes", label: "Notes", type: "textarea" },
];

type Stats = {
  active: number;
  mrr: number;
  arr: number;
  dueWeek: number;
  paused: number;
};

export default function Page() {
  const [stats, setStats] = useState<Stats>({
    active: 0,
    mrr: 0,
    arr: 0,
    dueWeek: 0,
    paused: 0,
  });

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("recurring_donations")
        .select("amount,frequency,status,next_payment_date")
        .limit(2000);
      if (!data) {
        return;
      }
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const weekAhead = new Date(today);
      weekAhead.setDate(weekAhead.getDate() + 7);
      let active = 0,
        mrr = 0,
        dueWeek = 0,
        paused = 0;
      data.forEach((r: any) => {
        if (r.status === "active") {
          active += 1;
          const amt = Number(r.amount ?? 0);
          const factor =
            r.frequency === "monthly"
              ? 1
              : r.frequency === "quarterly"
                ? 1 / 3
                : 1 / 12;
          mrr += amt * factor;
          if (r.next_payment_date) {
            const d = new Date(r.next_payment_date);
            if (d >= today && d <= weekAhead) {
              dueWeek += 1;
            }
          }
        } else if (r.status === "paused") {
          paused += 1;
        }
      });
      setStats({
        active,
        mrr: Math.round(mrr),
        arr: Math.round(mrr * 12),
        dueWeek,
        paused,
      });
    })();
  }, []);

  return (
    <AdminShell
      title="Recurring Donations"
      subtitle="Monthly · Quarterly · Yearly pledged plans"
    >
      <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi
          icon={<Repeat className="h-4 w-4" />}
          label="Active Plans"
          value={stats.active}
          sub={`${stats.paused} paused`}
          accent="from-emerald-600 to-emerald-500"
        />
        <Kpi
          icon={<IndianRupee className="h-4 w-4" />}
          label="MRR (est.)"
          value={`₹${stats.mrr.toLocaleString("en-IN")}`}
          sub="Normalized monthly"
          accent="from-primary/90 to-primary"
        />
        <Kpi
          icon={<TrendingUp className="h-4 w-4" />}
          label="ARR (est.)"
          value={`₹${stats.arr.toLocaleString("en-IN")}`}
          sub="MRR × 12"
          accent="from-amber-600 to-amber-500"
        />
        <Kpi
          icon={<CalendarClock className="h-4 w-4" />}
          label="Due in 7 days"
          value={stats.dueWeek}
          sub="Upcoming charges"
          accent="from-rose-600 to-rose-500"
        />
      </div>
      <ResourceTable
        table="recurring_donations"
        title="Recurring Plan"
        fields={fields}
        searchFields={["notes", "razorpay_subscription_id"]}
        statusField="status"
        statusFilterOptions={["active", "paused", "cancelled", "completed"]}
        totalField="amount"
      />
    </AdminShell>
  );
}

function Kpi({
  icon,
  label,
  value,
  sub,
  accent,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sub?: string;
  accent: string;
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div
        className={`absolute -right-8 -top-8 h-28 w-28 rounded-full bg-gradient-to-br ${accent} opacity-20 blur-2xl`}
      />
      <div className="relative flex items-start justify-between gap-2">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            {label}
          </div>
          <div className="mt-1 text-2xl font-bold">{value}</div>
          {sub && (
            <div className="mt-0.5 text-[11px] text-muted-foreground">
              {sub}
            </div>
          )}
        </div>
        <div
          className={`flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br ${accent} text-white shadow`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}
